import { useState } from "react";
import { html } from "react-strict-dom";
import {
  Dialog,
  Button,
  Text,
  Heading,
  Spinner,
  Alert,
  Card,
  Stack,
} from "@duro-app/ui";

export interface PublishDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly draftName: string;
  readonly slug: string;
  readonly publicHost: string;
  readonly signedIn: boolean;
  /** the draft already has a cloud home (re-publish updates it). */
  readonly alreadyOnline: boolean;
  readonly onExport: () => Promise<{ ok: boolean; error?: string }>;
  readonly onPublishOnline: () => Promise<{
    ok: boolean;
    url?: string;
    error?: string;
    /** What the publish left out or changed (e.g. a widget kind this
     *  editor doesn't know) — shown, never only logged. */
    warnings?: readonly string[];
  }>;
  readonly onSignInToPublish: () => void;
}

type Busy = null | "export" | "online";

/**
 * The publish fork: the builder is free, so publishing offers two paths —
 *   1. Download with git history (a .zip incl. .git) — no account needed.
 *   2. Publish online — sign in (if needed), then push to a hosted URL.
 */
export function PublishDialog({
  open,
  onClose,
  draftName,
  slug,
  publicHost,
  signedIn,
  alreadyOnline,
  onExport,
  onPublishOnline,
  onSignInToPublish,
}: PublishDialogProps) {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [exported, setExported] = useState(false);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<readonly string[]>([]);

  async function handleExport() {
    setBusy("export");
    setError(null);
    setExported(false);
    const r = await onExport();
    setBusy(null);
    if (r.ok) setExported(true);
    else setError(r.error ?? "Export failed.");
  }

  async function handleOnline() {
    setBusy("online");
    setError(null);
    const r = await onPublishOnline();
    setBusy(null);
    setWarnings(r.ok ? (r.warnings ?? []) : []);
    if (r.ok) setLiveUrl(r.url ?? `https://${slug}.${publicHost}`);
    else setError(r.error ?? "Publish failed.");
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal size="md">
        <Dialog.Header>
          <Dialog.Title>Publish “{draftName}”</Dialog.Title>
          <Dialog.Close aria-label="Close" />
        </Dialog.Header>
        <Dialog.Body>
          <Stack gap="ms">
            {error && (
              <Alert variant="error">
                {/* One line per problem: a blocked publish lists every fix. */}
                <Stack gap="xs">
                  {error.split("\n").map((line, i) => (
                    <Text key={i} variant="bodySm">
                      {line}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}

            {/* ── Option 1: download with history ── */}
            <Card variant="outlined">
              <Stack gap="sm" align="start">
                <Heading level={3} variant="headingSm">
                  Download with git history
                </Heading>
                <Text variant="bodySm" color="muted">
                  Get your whole site as a <html.code>.zip</html.code> —
                  including the <html.code>.git</html.code> folder, so your full
                  edit history comes with it. No account needed.
                </Text>
                <Button
                  variant="secondary"
                  disabled={busy !== null}
                  onClick={() => void handleExport()}
                >
                  {busy === "export" ? <Spinner size="sm" /> : "Download .zip"}
                </Button>
                {exported && (
                  <Text variant="caption" color="success">
                    Download started — unzip and <html.code>git log</html.code>{" "}
                    to see your history.
                  </Text>
                )}
              </Stack>
            </Card>

            {/* ── Option 2: publish online ── */}
            <Card variant="outlined">
              <Stack gap="sm" align="start">
                <Heading level={3} variant="headingSm">
                  {alreadyOnline ? "Update your live site" : "Publish online"}
                </Heading>
                <Text variant="bodySm" color="muted">
                  {signedIn
                    ? `Push your site live at ${slug}.${publicHost}.`
                    : "Sign in to publish your site to a public URL we host for you."}
                </Text>
                {signedIn ? (
                  <Button
                    variant="primary"
                    disabled={busy !== null}
                    onClick={() => void handleOnline()}
                  >
                    {busy === "online" ? (
                      <Spinner size="sm" />
                    ) : alreadyOnline ? (
                      "Update online"
                    ) : (
                      "Publish online"
                    )}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={onSignInToPublish}>
                    Sign in to publish
                  </Button>
                )}
                {liveUrl && (
                  <Text variant="caption" color="success">
                    Live at{" "}
                    <html.a href={liveUrl} target="_blank" rel="noreferrer">
                      {liveUrl}
                    </html.a>
                  </Text>
                )}
                {liveUrl && warnings.length > 0 && (
                  <Alert variant="warning">
                    Published, but not everything made it: {warnings.join("; ")}
                  </Alert>
                )}
              </Stack>
            </Card>
          </Stack>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
